// Helpers puros de la vista 360 del usuario (113-user-360-redesign): pestañas con deep link,
// indicadores del Resumen ("Membresías vigentes", "Reembolsos pendientes", "Requiere
// atención") y "Últimos movimientos". No llaman servicios ni tocan el DOM.

import RutUtils from './rut.utils';

// ─── Pestañas y deep link (?tab=, ?membership=) ─────────────────────────────

export const TAB_KEYS = ['resumen', 'mascotas', 'coberturas', 'perfil'];
export const DEFAULT_TAB = 'resumen';

// Pestaña a abrir según la query; una pestaña desconocida o ausente abre la por defecto.
export function resolveTab(query) {
  const tab = query && query.tab;
  return TAB_KEYS.includes(tab) ? tab : DEFAULT_TAB;
}

// Query para $router.replace: la pestaña más los extras que tengan valor (un `membership`
// vacío no se deja colgando en la URL).
export function tabQuery(tab, extra = {}) {
  const query = { tab };
  Object.keys(extra).forEach(key => {
    const value = extra[key];
    if (value !== null && value !== undefined && value !== '') query[key] = value;
  });
  return query;
}

// ─── Membresías vigentes «N de M» ───────────────────────────────────────────

const ACTIVE_STATUS = 2;
const PAYMENT_FAILED_STATUS = 3;
const HISTORIC_STATUS = 4;

// Moneda del plan → país del subtexto. Otra moneda se cuenta por su código.
const CURRENCY_COUNTRY_NAMES = { CLP: 'Chile', PEN: 'Perú', MXN: 'México' };

// Vigente = Activo (2); histórica = Desactivada (4); total = cualquier estado. Los países salen
// de la moneda de las filas vigentes del endpoint de uso, en orden de aparición.
export function countActiveMemberships(detailsPlan = [], usageRows = []) {
  const memberships = detailsPlan || [];
  const countries = [];
  (usageRows || []).forEach(row => {
    if (!row || !row.currency) return;
    const country = CURRENCY_COUNTRY_NAMES[row.currency] || row.currency;
    if (!countries.includes(country)) countries.push(country);
  });
  return {
    active: memberships.filter(m => m.status === ACTIVE_STATUS).length,
    total: memberships.length,
    historic: memberships.filter(m => m.status === HISTORIC_STATUS).length,
    countries
  };
}

// ─── Reembolsos pendientes ──────────────────────────────────────────────────

// Estados finales por `sequenceOrder`: Rechazado, Aprobado, Pagado, Pago inválido. Todo lo
// demás (Registrado 0, En revisión 100, Corregir 200, o un estado nuevo) cuenta como pendiente.
const FINAL_REFUND_SEQUENCES = [300, 400, 500, 600];

// Mismo criterio que refundStatusColorsMixin: "Por corregir" y "Corregir" no fallan por
// tilde/caja/espacios.
function normaliseStatusName(name) {
  return String(name || '')
    .normalize('NFD')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

// `requestUsage[].changelog` trae solo el nombre del estado; se traduce a `sequenceOrder` con
// el catálogo de GET /refunds/changelog/statuses. Un nombre fuera del catálogo es pendiente.
export function pendingRefundsCount(requestUsage = [], statuses = []) {
  const sequenceByName = {};
  (statuses || []).forEach(status => {
    sequenceByName[normaliseStatusName(status.name)] = status.sequenceOrder;
  });
  return (requestUsage || []).filter(refund => {
    const sequence = sequenceByName[normaliseStatusName(refund.changelog)];
    return !FINAL_REFUND_SEQUENCES.includes(sequence);
  }).length;
}

// ─── Días a vencer ──────────────────────────────────────────────────────────

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

// Día calendario en UTC para restar sin saltos por horario de verano.
const utcDay = (year, month, day) => Date.UTC(year, month - 1, day);

function todayUtcDay(today) {
  if (typeof today === 'string') {
    const match = ISO_DATE.exec(today);
    if (match) return utcDay(Number(match[1]), Number(match[2]), Number(match[3]));
  }
  const date = today instanceof Date ? today : new Date();
  // Fecha local del navegador (Chile para soporte), no la de UTC.
  return utcDay(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

// Días entre hoy y una fecha 'YYYY-MM-DD' (o ISO), leída por partes de texto: `new Date('2026-07-20')`
// se interpreta en UTC y en hora de Chile caería un día antes. null si la fecha no es válida.
export function daysUntil(isoDate, today = new Date()) {
  const match = ISO_DATE.exec(isoDate || '');
  if (!match) return null;
  const target = utcDay(Number(match[1]), Number(match[2]), Number(match[3]));
  return Math.round((target - todayUtcDay(today)) / MS_PER_DAY);
}

// ─── Fin de vigencia ────────────────────────────────────────────────────────

// membership-data entrega 'DD/MM/YY' (DateFormat.formatDateDDMMYYYY recorta el año a 2 dígitos);
// se acepta también 'DD/MM/YYYY'.
const DMY_DATE = /^(\d{2})\/(\d{2})\/(\d{2}|\d{4})$/;

// 'DD/MM/YY' o 'DD/MM/YYYY' → 'YYYY-MM-DD' (año de 2 dígitos = 20YY); null si no es una fecha (p. ej. '-').
export function dmyToIso(text) {
  const match = DMY_DATE.exec(text || '');
  if (!match) return null;
  const year = match[3].length === 2 ? `20${match[3]}` : match[3];
  return `${year}-${match[2]}-${match[1]}`;
}

// Fin de vigencia de una membresía de membership-data: la fecha de desactivación si existe;
// si no, la de expiración. 'YYYY-MM-DD' o null.
export function membershipEndDate(membership) {
  if (!membership) return null;
  return dmyToIso(membership.endDate) || dmyToIso(membership.expirationDate);
}

// Igual que membershipEndDate pero para una fila del endpoint de uso, cruzando por id con
// membership-data; si la membresía no está, usa el endDate que trae la fila.
export function usageRowEndDate(row, detailsPlan = []) {
  const membership = (detailsPlan || []).find(m => m.id === row.membershipId);
  return membershipEndDate(membership) || row.endDate || null;
}

// ─── Requiere atención ──────────────────────────────────────────────────────

const EXPIRING_SOON_DAYS = 30;
const NEAR_CAP_RATIO = 0.8;
const PERCENT = 100;

// Orden de prioridad de las alertas (menor = más importante).
const ALERT_PRIORITY = {
  'payment-failed': 0,
  'expiring-soon': 1,
  exhausted: 2,
  'near-cap': 3
};

const membershipDetail = (petName, planName) =>
  [petName, planName].filter(Boolean).join(' · ') || '—';

function expiringLabel(days) {
  if (days === 0) return 'Vence hoy';
  if (days === 1) return 'Vence en 1 día';
  return `Vence en ${days} días`;
}

function paymentFailedAlerts(detailsPlan) {
  return (detailsPlan || [])
    .filter(m => m.status === PAYMENT_FAILED_STATUS)
    .map(m => ({
      kind: 'payment-failed',
      label: 'Pago fallido',
      detail: membershipDetail(m.name, m.planName),
      membershipId: m.id,
      sortKey: 0
    }));
}

// Alertas de una fila vigente del endpoint de uso: vence pronto, agotado y tope ≥ 80 %. El
// tope ≥ 80 % no se repite si la membresía ya tiene un servicio agotado (sería la misma señal).
function usageRowAlerts(row, today, detailsPlan) {
  const alerts = [];
  const detail = membershipDetail(row.petName, row.planName);
  const days = daysUntil(usageRowEndDate(row, detailsPlan), today);
  if (days !== null && days >= 0 && days <= EXPIRING_SOON_DAYS) {
    alerts.push({ kind: 'expiring-soon', label: expiringLabel(days), detail, membershipId: row.membershipId, sortKey: days });
  }
  if (row.exhaustedCount > 0) {
    const label = row.exhaustedCount === 1 ? '1 servicio agotado' : `${row.exhaustedCount} servicios agotados`;
    alerts.push({ kind: 'exhausted', label, detail, membershipId: row.membershipId, sortKey: -row.exhaustedCount });
  } else if (row.maxUsageRatio >= NEAR_CAP_RATIO) {
    const percent = Math.round(row.maxUsageRatio * PERCENT);
    alerts.push({ kind: 'near-cap', label: `${percent} % del tope usado`, detail, membershipId: row.membershipId, sortKey: -row.maxUsageRatio });
  }
  return alerts;
}

// Alertas de "Requiere atención" ordenadas por prioridad: pago fallido > vigente que vence en
// ≤ 30 días > servicio agotado > tope ≥ 80 %. Dentro de un mismo tipo, la más urgente primero
// (la que vence antes, la de más agotados, la de mayor % usado). Lista vacía = "Todo en orden".
export function attentionAlerts(detailsPlan = [], usageRows = [], today = new Date()) {
  const alerts = [
    ...paymentFailedAlerts(detailsPlan),
    ...(usageRows || []).flatMap(row => usageRowAlerts(row, today, detailsPlan))
  ];
  return alerts
    .sort((a, b) => ALERT_PRIORITY[a.kind] - ALERT_PRIORITY[b.kind] || a.sortKey - b.sortKey)
    .map(({ kind, label, detail, membershipId }) => ({ kind, label, detail, membershipId }));
}

// ─── Últimos movimientos ────────────────────────────────────────────────────

// Los `n` usos de cobertura más recientes. `coverageUsage` (buildCoverageUsageRows) ya viene
// ordenado por fecha; se ordena otra vez por `dateRank` para no depender de eso. El orden de
// `Array.prototype.sort` es estable, así que ante la misma fecha se respeta el de llegada.
export function latestMovements(coverageUsage = [], n = 5) {
  return [...(coverageUsage || [])]
    .sort((a, b) => (b.dateRank || 0) - (a.dateRank || 0))
    .slice(0, n);
}

// Bandeja de reembolsos con la ficha del reembolso abierta (RefundView lee ?refund=).
export function refundDetailRoute(referenceIndex) {
  return { name: 'Refunds2', query: { refund: String(referenceIndex) } };
}

// ─── Copiar datos ───────────────────────────────────────────────────────────

const orDash = value => (value === null || value === undefined || value === '' || value === '-' ? '-' : value);
// Valor entre backticks: en Slack/ClickUp se pega con formato de código (consola).
const code = value => `\`${orDash(value)}\``;

// Datos del tutor para pegar en un ticket o chat (botón copiar de la cabecera).
export function tutorCopyText(info = {}, link = '') {
  // "Rut" para Chile (o sin país); "DNI" para el resto de los países.
  const identifierLabel = RutUtils.identifierLabel(info.country) === 'DNI' ? 'DNI' : 'Rut';
  return [
    `Id: ${code(info.userId)}`,
    `Nombre: ${code(info.name)}`,
    `Email: ${code(info.email)}`,
    `${identifierLabel}: ${code(info.rut)}`,
    // Sin backticks: así el link sigue siendo clickeable al pegarlo.
    `Link: ${orDash(link)}`
  ].join('\n');
}

// Datos de una membresía de membership-data (botón copiar de la tabla de membresías).
export function membershipCopyText(membership = {}) {
  const contract = membership.contratoCodigo
    ? `${membership.contratoCodigo}${membership.companyName ? ` (${membership.companyName})` : ''}`
    : 'B2C';
  return [
    `Id: ${code(membership.id)}`,
    `Id mascota: ${code(membership.petId)}`,
    `Mascota: ${code(membership.name)}`,
    `Plan: ${code(membership.planName)}`,
    `Contrato: ${code(contract)}`
  ].join('\n');
}
